import { useMemo, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, Volume2, VolumeX, ArrowDown } from "lucide-react";
import { Magnetic, CountUp } from "./effects";
import { scrollTo } from "./Navbar";

const lines = [
  { text: "I Turn Raw Footage", cls: "text-white" },
  { text: "Into Content People", cls: "text-white" },
  { text: "Can't Stop Watching.", cls: "text-gradient-blue" },
];

const stats = [
  { value: 40, suffix: "+", label: "Projects Delivered" },
  { value: 1, suffix: "M+", label: "Views Generated" },
  { value: 4, suffix: "+", label: "Years Experience" },
];

const HeroLine = ({ text, cls, i }) => (
  <span className="mask-line">
    <motion.span
      className={`block font-display font-semibold tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-[4.6rem] ${cls}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay: 0.55 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPlayer = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const particles = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        id: i,
        left: `${(i * 53) % 100}%`,
        top: `${(i * 37) % 100}%`,
        size: 2 + (i % 3),
        delay: (i % 7) * 0.8,
        dur: 7 + (i % 5),
      })),
    []
  );

  const toggleSound = () => {
    setMuted((m) => {
      if (videoRef.current) videoRef.current.muted = !m;
      return !m;
    });
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16"
    >
      {/* Ambient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 h-[560px] w-[560px] rounded-full bg-[#D4AF37]/14 blur-[140px] animate-blob" />
        <div className="absolute top-1/3 -right-40 h-[480px] w-[480px] rounded-full bg-[#F5DE81]/10 blur-[130px] animate-blob" style={{ animationDelay: "-6s" }} />
        <div className="absolute bottom-0 left-1/3 h-[380px] w-[380px] rounded-full bg-[#C9A227]/10 blur-[120px] animate-blob" style={{ animationDelay: "-3s" }} />
        {particles.map((p) => (
          <span
            key={p.id}
            className="absolute rounded-full bg-[#E8C15A]"
            style={{
              left: p.left,
              top: p.top,
              width: p.size,
              height: p.size,
              opacity: 0.3,
              animation: `particle-float ${p.dur}s ease-in-out ${p.delay}s infinite`,
            }}
          />
        ))}
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
          }}
        />
      </div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-14 lg:gap-10 items-center">
          <motion.div style={{ y: yText }}>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.35 }}
              className="inline-flex items-center gap-2.5 glass rounded-full px-4 py-2 mb-8"
              data-testid="hero-availability-badge"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F5DE81] opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#F5DE81]" />
              </span>
              <span className="text-[11px] uppercase tracking-[0.22em] text-slate-300">
                Available for new projects
              </span>
            </motion.div>

            <h1 data-testid="hero-headline" className="mb-7">
              {lines.map((l, i) => (
                <HeroLine key={l.text} text={l.text} cls={l.cls} i={i} />
              ))}
            </h1>

            <motion.p
              data-testid="hero-subheadline"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.15 }}
              className="text-sm sm:text-base text-slate-400 max-w-lg leading-relaxed mb-10"
            >
              Short-form edits, YouTube videos, commercials, motion graphics, and
              storytelling that drive attention and results.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.3 }}
              className="flex flex-wrap items-center gap-4 mb-14"
            >
              <Magnetic>
                <button
                  data-testid="hero-view-portfolio-btn"
                  onClick={() => scrollTo("#short-form")}
                  className="group inline-flex items-center gap-3 rounded-full bg-white text-[#050505] text-sm font-semibold px-7 py-3.5 hover:bg-[#F5DE81] transition-colors duration-300"
                >
                  View Portfolio
                  <ArrowDown size={16} className="group-hover:translate-y-0.5 transition-transform duration-300" />
                </button>
              </Magnetic>
              <Magnetic>
                <button
                  data-testid="hero-book-call-btn"
                  onClick={() => scrollTo("#contact")}
                  className="inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/50 text-white text-sm font-semibold px-7 py-3.5 hover:bg-[#D4AF37]/15 hover:border-[#D4AF37] transition-all duration-300 shadow-[0_0_30px_rgba(212,175,55,0.15)]"
                >
                  Book a Call
                </button>
              </Magnetic>
            </motion.div>

            <motion.div
              data-testid="hero-stats-row"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.5 }}
              className="flex flex-wrap gap-8 sm:gap-12"
            >
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-2xl sm:text-3xl font-semibold text-white">
                    <CountUp value={s.value} suffix={s.suffix} />
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mt-1.5">
                    {s.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Floating showreel player */}
          <motion.div
            style={{ y: yPlayer }}
            initial={{ opacity: 0, y: 60, rotate: 2 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ duration: 1.2, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto w-full max-w-[380px]"
            data-testid="hero-showreel-player"
          >
            <div className="absolute -inset-6 rounded-[2rem] bg-[#D4AF37]/20 blur-3xl animate-glow-pulse" />
            <motion.div
              whileHover={{ scale: 1.035, y: -8 }}
              transition={{ type: "spring", stiffness: 200, damping: 18 }}
              className="relative rounded-3xl overflow-hidden border border-white/10 bg-[#0a0a0c] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)] hover:shadow-[0_0_60px_rgba(212,175,55,0.35)] transition-shadow duration-500 group"
            >
              <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
                <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
                <span className="ml-3 text-[10px] uppercase tracking-[0.25em] text-slate-500">
                  showreel_2026.mp4
                </span>
              </div>
              <div className="relative aspect-[9/13]">
                <video
                  ref={videoRef}
                  src="/videos/talking-head.mp4"
                  poster="/thumbs/talking-head.jpg"
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
                <button
                  data-testid="hero-showreel-sound-toggle"
                  onClick={toggleSound}
                  aria-label="Toggle sound"
                  className="absolute bottom-4 right-4 glass rounded-full p-2.5 text-white hover:border-[#D4AF37]/60 transition-colors duration-300"
                >
                  {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
                </button>
                <div className="absolute bottom-4 left-4 flex items-center gap-2">
                  <span className="flex items-center justify-center h-8 w-8 rounded-full bg-[#D4AF37] shadow-[0_0_20px_rgba(212,175,55,0.6)]">
                    <Play size={13} className="text-white ml-0.5" fill="currentColor" />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-slate-300">
                    Live Preview
                  </span>
                </div>
              </div>
            </motion.div>
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-8 top-10 glass-accent rounded-2xl px-4 py-3 hidden sm:block"
            >
              <div className="font-display text-lg font-semibold text-[#F5DE81]">98%</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-400">Client Satisfaction</div>
            </motion.div>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -right-6 bottom-16 glass-accent rounded-2xl px-4 py-3 hidden sm:block"
            >
              <div className="font-display text-lg font-semibold text-white">4K</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-400">Delivery Quality</div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
